import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, CheckCircle, XCircle, Search, RefreshCw, FileText, Building, Calendar, User, Eye, X, Paperclip, Edit3, Download, Send, Forward, Printer, Copy, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdProposalReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, by_type: {}, by_magazine: {} });
  const [filters, setFilters] = useState({
    magazine: '',
    proposal_type: '',
    company_name: '',
    date_from: '',
    date_to: ''
  });
  
  // Detail view state
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Follow-up state
  const [showFollowup, setShowFollowup] = useState(false);
  const [followupLoading, setFollowupLoading] = useState(false);
  const [followupPreviewHtml, setFollowupPreviewHtml] = useState('');
  const [followupData, setFollowupData] = useState({
    proposal_id: '',
    additional_emails: '',
    custom_message: ''
  });

  // Edit & Resend state
  const [showEditResend, setShowEditResend] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editPreviewHtml, setEditPreviewHtml] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    proposal_id: '',
    magazine: '',
    contact_name: '',
    company_name: '',
    emails: '',
    proposal_type: '',
    proposal_date: '',
    ad_positions: [],
    discount_percent: 0,
    custom_introduction: '',
    custom_why_advertise: '',
    custom_terms_conditions: '',
    special_remarks: '',
    banner_image_url: '',
    media_kit_url: ''
  });

  const proposalTypeLabels = {
    'standard_tariff': 'Standard (Std. Tariff)',
    'custom_tariff': 'Standard (Custom Tariff)',
    'republic_day': 'Republic Day',
    'independence_day': 'Independence Day',
    'holi': 'Holi Special',
    'diwali': 'Diwali Special',
    'custom': 'Custom'
  };

  useEffect(() => {
    fetchProposals();
    fetchStats();
  }, []);

  const fetchProposals = async (searchFilters = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const hasFilters = Object.values(searchFilters).some(v => v);
      const response = hasFilters 
        ? await axios.post(`${API}/ad-proposals/search`, searchFilters, {
            headers: { Authorization: `Bearer ${token}` }
          })
        : await axios.get(`${API}/ad-proposals`, {
            headers: { Authorization: `Bearer ${token}` }
          });
      
      setProposals(response.data);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast.error('Failed to fetch proposals');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ad-proposals/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch the exact email HTML that was sent
  const fetchProposalPreview = async (proposal) => {
    setLoadingPreview(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ad-proposals/preview`, {
        magazine: proposal.magazine,
        proposal_type: proposal.proposal_type,
        contact_name: proposal.contact_name,
        company_name: proposal.company_name,
        emails: proposal.emails,
        proposal_date: proposal.proposal_date,
        ad_positions: proposal.ad_positions || [],
        discount_percent: proposal.discount_percent,
        terms_conditions: proposal.terms_conditions,
        special_remarks: proposal.special_remarks,
        banner_image_url: proposal.banner_image_url,
        introduction: proposal.introduction,
        why_advertise: proposal.why_advertise,
        // Frequency tariff data - MUST include for correct preview
        frequency_positions: proposal.frequency_positions || [],
        frequency_settings: proposal.frequency_settings || {},
        // Editable content
        circulation_text: proposal.circulation_text || '',
        signature_text: proposal.signature_text || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPreviewHtml(response.data.html);
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Failed to load email preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Open detail view
  const handleViewDetails = async (proposal) => {
    setSelectedProposal(proposal);
    setShowDetail(true);
    await fetchProposalPreview(proposal);
  };

  const handleSearch = () => {
    fetchProposals(filters);
  };

  const handleReset = () => {
    setFilters({
      magazine: '',
      proposal_type: '',
      company_name: '',
      date_from: '',
      date_to: ''
    });
    fetchProposals();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const calculateTotal = (positions) => {
    if (!positions || !Array.isArray(positions)) return 0;
    return positions.reduce((sum, pos) => sum + (pos.discounted_price || 0), 0);
  };

  // Follow-up functions
  const handleOpenFollowup = async (proposal) => {
    setSelectedProposal(proposal);
    setFollowupData({
      proposal_id: proposal.id,
      additional_emails: '',
      custom_message: ''
    });
    setShowFollowup(true);
    
    // Load follow-up preview
    setFollowupLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ad-proposals/followup/preview`, {
        proposal_id: proposal.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowupPreviewHtml(response.data.html);
    } catch (error) {
      console.error('Error loading follow-up preview:', error);
      toast.error('Failed to load follow-up preview');
    } finally {
      setFollowupLoading(false);
    }
  };

  const handleSendFollowup = async () => {
    if (!followupData.proposal_id) return;
    
    setFollowupLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ad-proposals/followup`, followupData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Follow-up sent to ${response.data.sent_count} recipient(s)!`);
      setShowFollowup(false);
      fetchProposals();
    } catch (error) {
      console.error('Error sending follow-up:', error);
      toast.error(error.response?.data?.detail || 'Failed to send follow-up');
    } finally {
      setFollowupLoading(false);
    }
  };

  const updateFollowupPreview = async () => {
    if (!followupData.proposal_id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ad-proposals/followup/preview`, {
        proposal_id: followupData.proposal_id,
        custom_message: followupData.custom_message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowupPreviewHtml(response.data.html);
    } catch (error) {
      console.error('Error updating preview:', error);
    }
  };

  // Edit & Resend functions
  const handleOpenEditResend = (proposal) => {
    setSelectedProposal(proposal);
    setEditData({
      proposal_id: proposal.id,
      magazine: proposal.magazine,
      contact_name: proposal.contact_name,
      company_name: proposal.company_name,
      emails: proposal.emails,
      proposal_type: proposal.proposal_type,
      proposal_date: new Date().toISOString().split('T')[0], // Today's date for resend
      ad_positions: proposal.ad_positions || [],
      discount_percent: proposal.discount_percent || 0,
      custom_introduction: proposal.custom_introduction || '',
      custom_why_advertise: proposal.custom_why_advertise || '',
      custom_terms_conditions: proposal.custom_terms_conditions || '',
      special_remarks: proposal.special_remarks || '',
      banner_image_url: proposal.banner_image_url || '',
      media_kit_url: proposal.media_kit_url || ''
    });
    setShowEditResend(true);
    loadEditPreview(proposal);
  };

  // Copy as Draft - Navigate to Ad Proposal page with preloaded data
  // Copy as Draft with save option
  const handleCopyAsDraft = async (proposal, saveAsDraft = null) => {
    // If saveAsDraft is null, show confirmation dialog
    if (saveAsDraft === null) {
      setSelectedProposal(proposal);
      setShowCopyDialog(true);
      return;
    }
    
    setShowCopyDialog(false);
    
    try {
      const token = localStorage.getItem('token');
      
      if (saveAsDraft) {
        // Save as draft in database first
        const draftResponse = await axios.post(`${API}/ad-proposals/drafts/from-copy/${proposal.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(`Draft saved! "${draftResponse.data.draft.draft_name}"`);
        
        // Now open in editor with the draft data
        sessionStorage.setItem('proposalDraft', JSON.stringify({
          ...draftResponse.data.draft,
          action: 'copy',
          draft_id: draftResponse.data.draft_id,
          copied_from: {
            proposal_id: proposal.id,
            original_company: proposal.company_name,
            sent_at: proposal.sent_at
          }
        }));
      } else {
        // Just open in editor without saving draft
        const response = await axios.get(`${API}/ad-proposals/copy/${proposal.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        sessionStorage.setItem('proposalDraft', JSON.stringify({
          ...response.data,
          action: 'copy'
        }));
      }
      
      navigate('/advertisement-proposal');
    } catch (error) {
      console.error('Error copying proposal:', error);
      toast.error('Failed to copy proposal');
    }
  };

  // Forward - Similar to copy but with different intent
  const handleForward = async (proposal) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ad-proposals/forward/${proposal.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store forward data in sessionStorage
      sessionStorage.setItem('proposalDraft', JSON.stringify({
        ...response.data,
        action: 'forward'
      }));
      
      toast.success('Ready to forward! Redirecting...');
      navigate('/advertisement-proposal');
    } catch (error) {
      console.error('Error forwarding proposal:', error);
      toast.error('Failed to forward proposal');
    }
  };

  // Save as Template from reports
  const handleSaveAsTemplate = async (proposal) => {
    const templateName = prompt('Enter template name:', `${proposal.proposal_type_label || proposal.proposal_type} - ${new Date().getFullYear()}`);
    if (!templateName) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/ad-proposals/templates/from-proposal/${proposal.id}?template_name=${encodeURIComponent(templateName)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Template "${templateName}" saved!`);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save as template');
    }
  };

  // Copy Dialog state
  const [showCopyDialog, setShowCopyDialog] = useState(false);

  const loadEditPreview = async (proposal) => {
    setEditLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ad-proposals/preview`, {
        magazine: proposal.magazine,
        contact_name: proposal.contact_name,
        company_name: proposal.company_name,
        proposal_type: proposal.proposal_type,
        proposal_date: new Date().toISOString().split('T')[0],
        ad_positions: proposal.ad_positions || [],
        discount_percent: proposal.discount_percent || 0,
        custom_introduction: proposal.custom_introduction || '',
        custom_why_advertise: proposal.custom_why_advertise || '',
        custom_terms_conditions: proposal.custom_terms_conditions || '',
        special_remarks: proposal.special_remarks || '',
        banner_image_url: proposal.banner_image_url || '',
        // Frequency tariff data
        frequency_positions: proposal.frequency_positions || [],
        frequency_settings: proposal.frequency_settings || {},
        // Editable content
        introduction: proposal.introduction || '',
        why_advertise: proposal.why_advertise || '',
        circulation_text: proposal.circulation_text || '',
        signature_text: proposal.signature_text || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditPreviewHtml(response.data.html);
    } catch (error) {
      console.error('Error loading edit preview:', error);
    } finally {
      setEditLoading(false);
    }
  };

  const updateEditPreview = async () => {
    setEditLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ad-proposals/preview`, {
        magazine: editData.magazine,
        contact_name: editData.contact_name,
        company_name: editData.company_name,
        proposal_type: editData.proposal_type,
        proposal_date: editData.proposal_date,
        ad_positions: editData.ad_positions,
        discount_percent: editData.discount_percent,
        custom_introduction: editData.custom_introduction,
        custom_why_advertise: editData.custom_why_advertise,
        custom_terms_conditions: editData.custom_terms_conditions,
        special_remarks: editData.special_remarks,
        banner_image_url: editData.banner_image_url,
        // Frequency tariff data
        frequency_positions: editData.frequency_positions || [],
        frequency_settings: editData.frequency_settings || {},
        // Editable content
        introduction: editData.introduction || '',
        why_advertise: editData.why_advertise || '',
        circulation_text: editData.circulation_text || '',
        signature_text: editData.signature_text || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditPreviewHtml(response.data.html);
      // Also update main preview when in edit mode within detail modal
      if (editMode) {
        setPreviewHtml(response.data.html);
      }
    } catch (error) {
      console.error('Error updating preview:', error);
      toast.error('Failed to update preview');
    } finally {
      setEditLoading(false);
    }
  };

  const handleResendProposal = async () => {
    if (!editData.emails) {
      toast.error('Please enter at least one email address');
      return;
    }

    setEditLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ad-proposals/send`, {
        ...editData,
        sent_by: 'admin'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Proposal resent successfully!');
      setShowEditResend(false);
      setShowDetail(false);
      setEditMode(false);
      fetchProposals();
    } catch (error) {
      console.error('Error resending proposal:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend proposal');
    } finally {
      setEditLoading(false);
    }
  };

  // Print/Download PDF functions
  const handlePrintProposal = async (proposal) => {
    setLoadingPreview(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ad-proposals/preview`, {
        magazine: proposal.magazine,
        contact_name: proposal.contact_name,
        company_name: proposal.company_name,
        proposal_type: proposal.proposal_type,
        proposal_date: proposal.proposal_date,
        ad_positions: proposal.ad_positions || [],
        discount_percent: proposal.discount_percent || 0,
        custom_introduction: proposal.custom_introduction || '',
        custom_why_advertise: proposal.custom_why_advertise || '',
        custom_terms_conditions: proposal.custom_terms_conditions || '',
        special_remarks: proposal.special_remarks || '',
        banner_image_url: proposal.banner_image_url || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open print window
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Advertisement Proposal - ${proposal.company_name}</title>
          <style>
            @media print {
              body { margin: 0; padding: 10mm; }
              @page { size: A4; margin: 10mm; }
            }
            body { font-family: Arial, sans-serif; }
          </style>
        </head>
        <body>
          ${response.data.html}
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      console.error('Error loading proposal for print:', error);
      toast.error('Failed to load proposal for printing');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownloadPDF = async (proposal) => {
    setLoadingPreview(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ad-proposals/generate-pdf`, {
        magazine: proposal.magazine,
        contact_name: proposal.contact_name,
        company_name: proposal.company_name,
        proposal_type: proposal.proposal_type,
        proposal_date: proposal.proposal_date,
        ad_positions: proposal.ad_positions || [],
        discount_percent: proposal.discount_percent || 0,
        custom_introduction: proposal.custom_introduction || '',
        custom_why_advertise: proposal.custom_why_advertise || '',
        custom_terms_conditions: proposal.custom_terms_conditions || '',
        special_remarks: proposal.special_remarks || '',
        banner_image_url: proposal.banner_image_url || ''
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Ad_Proposal_${proposal.company_name.replace(/\s+/g, '_')}_${proposal.proposal_date}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>
              Advertisement Proposal Reports
            </h1>
          </div>
          <div className="flex space-x-3">
            <Button onClick={() => navigate('/advertisement-proposal')} style={{ backgroundColor: '#7c3aed' }}>
              <FileText className="mr-2" size={18} />
              Create New Proposal
            </Button>
            <Button 
              onClick={() => { fetchProposals(); fetchStats(); }} 
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-3">
              <FileText className="text-blue-600" size={24} />
              <div>
                <p className="text-2xl font-bold text-blue-800">{stats.total}</p>
                <p className="text-sm text-blue-600">Total Proposals</p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center space-x-3">
              <CheckCircle className="text-green-600" size={24} />
              <div>
                <p className="text-2xl font-bold text-green-800">{stats.sent}</p>
                <p className="text-sm text-green-600">Sent Successfully</p>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="flex items-center space-x-3">
              <Mail className="text-purple-600" size={24} />
              <div>
                <p className="text-2xl font-bold text-purple-800">{stats.by_magazine?.['Aabkari Times'] || 0}</p>
                <p className="text-sm text-purple-600">Aabkari Times</p>
              </div>
            </div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="flex items-center space-x-3">
              <Mail className="text-orange-600" size={24} />
              <div>
                <p className="text-2xl font-bold text-orange-800">{stats.by_magazine?.['Sugar Times'] || 0}</p>
                <p className="text-sm text-orange-600">Sugar Times</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-8 border">
          <h2 className="font-semibold mb-4 flex items-center">
            <Search className="mr-2" size={18} />
            Search Proposals
          </h2>
          <div className="grid grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Magazine</Label>
              <select
                value={filters.magazine}
                onChange={(e) => setFilters({ ...filters, magazine: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All Magazines</option>
                <option value="Aabkari Times">Aabkari Times</option>
                <option value="Sugar Times">Sugar Times</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Proposal Type</Label>
              <select
                value={filters.proposal_type}
                onChange={(e) => setFilters({ ...filters, proposal_type: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All Types</option>
                {Object.entries(proposalTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input 
                value={filters.company_name}
                onChange={(e) => setFilters({ ...filters, company_name: e.target.value })}
                placeholder="Search company..."
              />
            </div>
            <div className="space-y-2">
              <Label>Date From</Label>
              <Input 
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Date To</Label>
              <Input 
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <Button variant="outline" onClick={handleReset}>Reset</Button>
            <Button onClick={handleSearch} style={{ backgroundColor: '#7c3aed' }}>
              <Search className="mr-2" size={16} />
              Search
            </Button>
          </div>
        </div>

        {/* Proposals Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 text-left text-sm font-semibold text-gray-700">Date</th>
                <th className="p-3 text-left text-sm font-semibold text-gray-700">Magazine</th>
                <th className="p-3 text-left text-sm font-semibold text-gray-700">Type</th>
                <th className="p-3 text-left text-sm font-semibold text-gray-700">Recipient</th>
                <th className="p-3 text-left text-sm font-semibold text-gray-700">Company</th>
                <th className="p-3 text-left text-sm font-semibold text-gray-700">Sent From</th>
                <th className="p-3 text-right text-sm font-semibold text-gray-700">Amount</th>
                <th className="p-3 text-center text-sm font-semibold text-gray-700">Status</th>
                <th className="p-3 text-center text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="p-8 text-center">
                    <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                    <p className="text-gray-500">Loading proposals...</p>
                  </td>
                </tr>
              ) : proposals.length === 0 ? (
                <tr>
                  <td colSpan="9" className="p-8 text-center text-gray-500">
                    No proposals found
                  </td>
                </tr>
              ) : (
                proposals.map((proposal, index) => (
                  <tr key={proposal.id || index} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => handleViewDetails(proposal)}>
                    <td className="p-3 text-sm">{formatDate(proposal.sent_at)}</td>
                    <td className="p-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        proposal.magazine === 'Aabkari Times' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {proposal.magazine}
                      </span>
                    </td>
                    <td className="p-3 text-sm">{proposalTypeLabels[proposal.proposal_type] || proposal.proposal_type}</td>
                    <td className="p-3 text-sm font-medium">{proposal.contact_name}</td>
                    <td className="p-3 text-sm text-gray-600">{proposal.company_name}</td>
                    <td className="p-3 text-sm text-indigo-600">
                      {proposal.sender_email ? (
                        <span title={proposal.sender_name || proposal.sender_email}>
                          {proposal.sender_email}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">N/A</span>
                      )}
                    </td>
                    <td className="p-3 text-sm text-right font-semibold">{formatCurrency(calculateTotal(proposal.ad_positions))}</td>
                    <td className="p-3 text-center">
                      {proposal.status === 'sent' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle className="mr-1" size={12} />
                          Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                          <XCircle className="mr-1" size={12} />
                          Failed
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center space-x-1 flex-wrap gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleViewDetails(proposal); }}
                          title="View / Edit / Resend"
                        >
                          <Eye size={14} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          onClick={(e) => { e.stopPropagation(); handleCopyAsDraft(proposal); }}
                          title="Copy as New Draft"
                        >
                          <Copy size={14} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          onClick={(e) => { e.stopPropagation(); handleOpenFollowup(proposal); }}
                          title="Send Follow Up"
                        >
                          <Forward size={14} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          onClick={(e) => { e.stopPropagation(); handlePrintProposal(proposal); }}
                          title="Print"
                        >
                          <Printer size={14} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={(e) => { e.stopPropagation(); handleDownloadPDF(proposal); }}
                          title="Download PDF"
                        >
                          <Download size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Detail Modal with Edit, Print, Resend functionality */}
        {showDetail && selectedProposal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
              {/* Header */}
              <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center">
                  <FileText className="mr-2" size={24} />
                  Proposal Details - {selectedProposal.company_name}
                </h2>
                <div className="flex items-center space-x-3">
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-white border-white hover:bg-white hover:text-purple-600"
                    onClick={() => handlePrintProposal(selectedProposal)}
                  >
                    <Printer className="mr-1" size={14} />
                    Print
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-white border-white hover:bg-white hover:text-purple-600"
                    onClick={() => handleCopyAsDraft(selectedProposal)}
                  >
                    <Copy className="mr-1" size={14} />
                    Copy as Draft
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-white border-white hover:bg-white hover:text-purple-600"
                    onClick={() => handleSaveAsTemplate(selectedProposal)}
                  >
                    <FileText className="mr-1" size={14} />
                    Save as Template
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-white border-white hover:bg-white hover:text-purple-600"
                    onClick={() => handleDownloadPDF(selectedProposal)}
                  >
                    <Download className="mr-1" size={14} />
                    Download PDF
                  </Button>
                  <button onClick={() => { setShowDetail(false); setSelectedProposal(null); setPreviewHtml(''); setEditMode(false); }} className="text-white hover:text-gray-200 ml-2">
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <div className="flex h-[calc(95vh-130px)]">
                {/* Left Panel - Proposal Info / Edit Mode */}
                <div className="w-2/5 border-r overflow-y-auto p-4 bg-gray-50">
                  {/* Edit Mode Toggle */}
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4 flex items-center justify-between">
                    <span className="text-sm text-blue-700 font-medium">
                      {editMode ? '✏️ Edit Mode - Make changes and resend' : '👁️ View Mode - Viewing sent proposal'}
                    </span>
                    <Button 
                      size="sm" 
                      variant={editMode ? "default" : "outline"}
                      onClick={async () => {
                        if (!editMode) {
                          // Entering edit mode - populate edit data from selected proposal
                          // Fetch default template content for the proposal type
                          let defaultIntro = '';
                          let defaultWhy = '';
                          try {
                            const token = localStorage.getItem('token');
                            const response = await axios.get(
                              `${API}/ad-proposals/defaults/${selectedProposal.proposal_type}?magazine=${selectedProposal.magazine}`,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            defaultIntro = response.data.introduction || '';
                            defaultWhy = response.data.why_advertise || '';
                          } catch (e) {
                            console.error('Error fetching defaults:', e);
                          }
                          
                          setEditData({
                            ...selectedProposal,
                            proposal_date: new Date().toISOString().split('T')[0],
                            // Load from proposal if exists, otherwise use defaults
                            custom_introduction: selectedProposal.introduction || selectedProposal.custom_introduction || defaultIntro,
                            custom_why_advertise: selectedProposal.why_advertise || selectedProposal.custom_why_advertise || defaultWhy,
                            // Load terms_conditions - use the actual stored value
                            custom_terms_conditions: selectedProposal.terms_conditions || selectedProposal.custom_terms_conditions || '',
                            // Special remarks
                            special_remarks: selectedProposal.special_remarks || ''
                          });
                        }
                        setEditMode(!editMode);
                      }}
                      className={editMode ? 'bg-blue-600' : ''}
                    >
                      <Edit3 className="mr-1" size={14} />
                      {editMode ? 'Exit Edit' : 'Edit & Resend'}
                    </Button>
                  </div>

                  {editMode ? (
                    /* Edit Mode - Editable Fields */
                    <>
                      {/* Recipient Details */}
                      <div className="bg-white p-4 rounded-lg border mb-4">
                        <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
                          <User className="mr-2" size={18} />
                          Recipient Details
                        </h3>
                        <div className="space-y-3 text-sm">
                          <div>
                            <Label className="text-xs">Contact Name</Label>
                            <Input 
                              value={editData.contact_name || ''}
                              onChange={(e) => setEditData({ ...editData, contact_name: e.target.value })}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Company Name</Label>
                            <Input 
                              value={editData.company_name || ''}
                              onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Email(s) - Comma separated</Label>
                            <Input 
                              value={editData.emails || ''}
                              onChange={(e) => setEditData({ ...editData, emails: e.target.value })}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Proposal Date (for resend)</Label>
                            <Input 
                              type="date"
                              value={editData.proposal_date || ''}
                              onChange={(e) => setEditData({ ...editData, proposal_date: e.target.value })}
                              className="text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Custom Content */}
                      <div className="bg-white p-4 rounded-lg border mb-4">
                        <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
                          <Edit3 className="mr-2" size={18} />
                          Customize Content
                        </h3>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs">Introduction</Label>
                            <Textarea 
                              value={editData.custom_introduction || ''}
                              onChange={(e) => setEditData({ ...editData, custom_introduction: e.target.value })}
                              rows={2}
                              className="text-sm"
                              placeholder="Custom introduction..."
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Why Advertise</Label>
                            <Textarea 
                              value={editData.custom_why_advertise || ''}
                              onChange={(e) => setEditData({ ...editData, custom_why_advertise: e.target.value })}
                              rows={2}
                              className="text-sm"
                              placeholder="Why advertise with us..."
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Terms & Conditions</Label>
                            <Textarea 
                              value={editData.custom_terms_conditions || ''}
                              onChange={(e) => setEditData({ ...editData, custom_terms_conditions: e.target.value })}
                              rows={2}
                              className="text-sm"
                              placeholder="Terms..."
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Special Remarks</Label>
                            <Textarea 
                              value={editData.special_remarks || ''}
                              onChange={(e) => setEditData({ ...editData, special_remarks: e.target.value })}
                              rows={2}
                              className="text-sm"
                              placeholder="Any special remarks..."
                            />
                          </div>
                        </div>
                      </div>

                      {/* Ad Positions - Editable Table */}
                      <div className="bg-white p-4 rounded-lg border mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-blue-700 flex items-center">
                            <FileText className="mr-2" size={18} />
                            Advertisement Tariff
                          </h3>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const newPosition = {
                                position: '',
                                page_type: 'Color',
                                insertions: '1 Issue',
                                std_price: 0,
                                discounted_price: 0,
                                period: ''
                              };
                              setEditData({
                                ...editData,
                                ad_positions: [...(editData.ad_positions || []), newPosition]
                              });
                            }}
                            className="text-green-600"
                          >
                            + Add Position
                          </Button>
                        </div>
                        
                        <div className="space-y-3">
                          {(editData.ad_positions || []).map((pos, idx) => (
                            <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-blue-600">Position {idx + 1}</span>
                                <button
                                  onClick={() => {
                                    const newPositions = editData.ad_positions.filter((_, i) => i !== idx);
                                    setEditData({ ...editData, ad_positions: newPositions });
                                  }}
                                  className="text-red-500 hover:text-red-700 text-xs"
                                >
                                  ✕ Remove
                                </button>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs">Position Name</Label>
                                  <select
                                    value={pos.position || ''}
                                    onChange={(e) => {
                                      const newPositions = [...editData.ad_positions];
                                      newPositions[idx].position = e.target.value;
                                      setEditData({ ...editData, ad_positions: newPositions });
                                    }}
                                    className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                                  >
                                    <option value="">Select Position</option>
                                    <option value="Back Cover">Back Cover</option>
                                    <option value="Front Inside Cover">Front Inside Cover</option>
                                    <option value="Back Inside Cover">Back Inside Cover</option>
                                    <option value="Full Page (Inside)">Full Page (Inside)</option>
                                    <option value="Half Page">Half Page</option>
                                    <option value="Quarter Page">Quarter Page</option>
                                    <option value="Centre Spread">Centre Spread</option>
                                    <option value="Bookmark">Bookmark</option>
                                    <option value="Strip Ad">Strip Ad</option>
                                  </select>
                                </div>
                                <div>
                                  <Label className="text-xs">Type</Label>
                                  <select
                                    value={pos.page_type || 'Color'}
                                    onChange={(e) => {
                                      const newPositions = [...editData.ad_positions];
                                      newPositions[idx].page_type = e.target.value;
                                      setEditData({ ...editData, ad_positions: newPositions });
                                    }}
                                    className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                                  >
                                    <option value="Color">Color</option>
                                    <option value="B&W">B&W</option>
                                  </select>
                                </div>
                                <div>
                                  <Label className="text-xs">Insertions</Label>
                                  <Input
                                    value={pos.insertions || ''}
                                    onChange={(e) => {
                                      const newPositions = [...editData.ad_positions];
                                      newPositions[idx].insertions = e.target.value;
                                      setEditData({ ...editData, ad_positions: newPositions });
                                    }}
                                    placeholder="e.g., 3 Issues"
                                    className="h-8 text-xs"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Period</Label>
                                  <Input
                                    value={pos.period || ''}
                                    onChange={(e) => {
                                      const newPositions = [...editData.ad_positions];
                                      newPositions[idx].period = e.target.value;
                                      setEditData({ ...editData, ad_positions: newPositions });
                                    }}
                                    placeholder="e.g., Jan-Mar 2025"
                                    className="h-8 text-xs"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Std. Rate (₹)</Label>
                                  <Input
                                    type="number"
                                    value={pos.std_price || 0}
                                    onChange={(e) => {
                                      const newPositions = [...editData.ad_positions];
                                      newPositions[idx].std_price = parseInt(e.target.value) || 0;
                                      setEditData({ ...editData, ad_positions: newPositions });
                                    }}
                                    className="h-8 text-xs"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Offer Price (₹)</Label>
                                  <Input
                                    type="number"
                                    value={pos.discounted_price || 0}
                                    onChange={(e) => {
                                      const newPositions = [...editData.ad_positions];
                                      newPositions[idx].discounted_price = parseInt(e.target.value) || 0;
                                      setEditData({ ...editData, ad_positions: newPositions });
                                    }}
                                    className="h-8 text-xs font-medium text-green-600"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {/* Total */}
                          {(editData.ad_positions || []).length > 0 && (
                            <div className="flex justify-between items-center bg-blue-50 p-3 rounded-lg">
                              <span className="font-semibold text-blue-700">Total Amount:</span>
                              <span className="font-bold text-green-600 text-lg">
                                ₹{(editData.ad_positions || []).reduce((sum, pos) => sum + (pos.discounted_price || 0), 0).toLocaleString()}/-
                                <span className="text-sm font-normal text-gray-500 ml-1">+ GST 5%</span>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-2">
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={updateEditPreview}
                          disabled={editLoading}
                        >
                          <RefreshCw className={`mr-2 ${editLoading ? 'animate-spin' : ''}`} size={14} />
                          Update Preview
                        </Button>
                        <Button 
                          className="w-full"
                          style={{ backgroundColor: '#2563eb' }}
                          onClick={handleResendProposal}
                          disabled={editLoading}
                        >
                          <Send className="mr-2" size={16} />
                          {editLoading ? 'Sending...' : 'Resend Proposal'}
                        </Button>
                      </div>
                    </>
                  ) : (
                    /* View Mode - Read-only Info */
                    <>
                      {/* Basic Info */}
                      <div className="bg-white p-4 rounded-lg border mb-4">
                        <h3 className="font-semibold text-purple-700 mb-3 flex items-center">
                          <User className="mr-2" size={18} />
                          Recipient Information
                        </h3>
                        <div className="space-y-2 text-sm">
                          <p><strong>Contact:</strong> {selectedProposal.contact_name}</p>
                          <p><strong>Company:</strong> {selectedProposal.company_name}</p>
                          <p><strong>Email(s):</strong> {selectedProposal.emails}</p>
                          <p><strong>Date:</strong> {selectedProposal.proposal_date}</p>
                          <p><strong>Sent At:</strong> {formatDate(selectedProposal.sent_at)}</p>
                          <p><strong>Sent By:</strong> {selectedProposal.sent_by}</p>
                          <p><strong>Sent From:</strong> <span className="text-indigo-600">{selectedProposal.sender_email || 'N/A'}</span></p>
                        </div>
                      </div>

                      {/* Proposal Type */}
                      <div className="bg-white p-4 rounded-lg border mb-4">
                        <h3 className="font-semibold text-purple-700 mb-3 flex items-center">
                          <Mail className="mr-2" size={18} />
                          Proposal Details
                        </h3>
                        <div className="space-y-2 text-sm">
                          <p><strong>Magazine:</strong> 
                            <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                              selectedProposal.magazine === 'Aabkari Times' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {selectedProposal.magazine}
                            </span>
                          </p>
                          <p><strong>Type:</strong> {proposalTypeLabels[selectedProposal.proposal_type] || selectedProposal.proposal_type}</p>
                          <p><strong>Total Amount:</strong> <span className="text-green-600 font-bold">{formatCurrency(calculateTotal(selectedProposal.ad_positions))}</span> + GST 5%</p>
                          {selectedProposal.discount_percent > 0 && (
                            <p><strong>Discount Applied:</strong> <span className="text-orange-600">{selectedProposal.discount_percent}%</span></p>
                          )}
                        </div>
                      </div>

                      {/* Ad Positions */}
                      <div className="bg-white p-4 rounded-lg border mb-4">
                        <h3 className="font-semibold text-purple-700 mb-3">Advertisement Positions</h3>
                        <div className="space-y-2">
                          {selectedProposal.ad_positions?.map((pos, idx) => (
                            <div key={idx} className="bg-gray-50 p-2 rounded text-xs">
                              <div className="flex justify-between">
                                <span className="font-medium">{pos.position}</span>
                                <span className="text-green-600 font-bold">{formatCurrency(pos.discounted_price)}</span>
                              </div>
                              <div className="text-gray-500 mt-1">
                                {pos.page_type} | {pos.insertions} {pos.period && `| ${pos.period}`}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Attachments */}
                      <div className="bg-white p-4 rounded-lg border mb-4">
                        <h3 className="font-semibold text-purple-700 mb-3 flex items-center">
                          <Paperclip className="mr-2" size={18} />
                          Attachments
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between bg-blue-50 p-2 rounded">
                            <span>📄 PDF Proposal Copy</span>
                            <span className="text-green-600 text-xs">✓ Attached</span>
                          </div>
                          {selectedProposal.media_kit_url ? (
                            <div className="flex items-center justify-between bg-green-50 p-2 rounded">
                              <span>📎 Media Kit</span>
                              <a href={selectedProposal.media_kit_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs hover:underline">
                                View Link
                              </a>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between bg-gray-50 p-2 rounded text-gray-400">
                              <span>📎 Media Kit</span>
                              <span className="text-xs">Not attached</span>
                            </div>
                          )}
                          {selectedProposal.banner_image_url ? (
                            <div className="flex items-center justify-between bg-orange-50 p-2 rounded">
                              <span>🎨 Banner Image</span>
                              <a href={selectedProposal.banner_image_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs hover:underline">
                                View Image
                              </a>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between bg-gray-50 p-2 rounded text-gray-400">
                              <span>🎨 Banner Image</span>
                              <span className="text-xs">Not used</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Customizations */}
                      {(selectedProposal.introduction || selectedProposal.why_advertise || selectedProposal.terms_conditions || selectedProposal.special_remarks) && (
                        <div className="bg-white p-4 rounded-lg border mb-4">
                          <h3 className="font-semibold text-purple-700 mb-3 flex items-center">
                            <Edit3 className="mr-2" size={18} />
                            Customizations Made
                          </h3>
                          <div className="space-y-3 text-sm">
                            {selectedProposal.introduction && (
                              <div>
                                <p className="font-medium text-gray-700">Introduction:</p>
                                <p className="text-gray-600 text-xs bg-gray-50 p-2 rounded mt-1">{selectedProposal.introduction.substring(0, 150)}...</p>
                              </div>
                            )}
                            {selectedProposal.why_advertise && (
                              <div>
                                <p className="font-medium text-gray-700">Why Advertise:</p>
                                <p className="text-gray-600 text-xs bg-gray-50 p-2 rounded mt-1">{selectedProposal.why_advertise.substring(0, 100)}...</p>
                              </div>
                            )}
                            {selectedProposal.terms_conditions && (
                              <div>
                                <p className="font-medium text-gray-700">Terms & Conditions:</p>
                                <p className="text-gray-600 text-xs bg-gray-50 p-2 rounded mt-1 whitespace-pre-line">{selectedProposal.terms_conditions.substring(0, 150)}...</p>
                              </div>
                            )}
                            {selectedProposal.special_remarks && (
                              <div>
                                <p className="font-medium text-gray-700">Special Remarks:</p>
                                <p className="text-gray-600 text-xs bg-yellow-50 p-2 rounded mt-1">{selectedProposal.special_remarks}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Quick Actions */}
                      <div className="space-y-2">
                        <Button 
                          variant="outline"
                          className="w-full text-orange-600 border-orange-300 hover:bg-orange-50"
                          onClick={() => handleOpenFollowup(selectedProposal)}
                        >
                          <Forward className="mr-2" size={16} />
                          Send Follow-up Email
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {/* Right Panel - Email Preview */}
                <div className="w-3/5 overflow-auto bg-gray-100 p-4">
                  <div className="bg-white rounded-lg shadow mb-4 p-3">
                    <h3 className="font-semibold text-gray-700 flex items-center">
                      <Eye className="mr-2" size={18} />
                      {editMode ? 'Preview (Click "Update Preview" to see changes)' : 'Exact Email Sent to Recipient'}
                    </h3>
                  </div>
                  
                  {loadingPreview ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <RefreshCw className="animate-spin mx-auto mb-3" size={32} />
                        <p className="text-gray-600">Loading email preview...</p>
                      </div>
                    </div>
                  ) : previewHtml ? (
                    <div className="bg-white rounded-lg shadow-lg mx-auto" style={{ maxWidth: '700px' }}>
                      <div 
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                        className="email-preview"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64">
                      <p className="text-gray-500">Failed to load email preview</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Follow-up Modal */}
        {showFollowup && selectedProposal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
              {/* Header */}
              <div className="bg-orange-500 text-white p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center">
                  <Forward className="mr-2" size={24} />
                  Send Follow-up - {selectedProposal.company_name}
                </h2>
                <button onClick={() => setShowFollowup(false)} className="text-white hover:text-gray-200">
                  <X size={24} />
                </button>
              </div>
              
              <div className="flex h-[calc(95vh-120px)]">
                {/* Left Panel - Follow-up Settings */}
                <div className="w-2/5 border-r overflow-y-auto p-4 bg-gray-50">
                  {/* Original Recipient Info */}
                  <div className="bg-white p-4 rounded-lg border mb-4">
                    <h3 className="font-semibold text-orange-700 mb-3 flex items-center">
                      <User className="mr-2" size={18} />
                      Original Recipient
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p><strong>Contact:</strong> {selectedProposal.contact_name}</p>
                      <p><strong>Company:</strong> {selectedProposal.company_name}</p>
                      <p><strong>Email(s):</strong> {selectedProposal.emails}</p>
                      <p><strong>Original Date:</strong> {selectedProposal.proposal_date}</p>
                    </div>
                  </div>

                  {/* Additional Emails */}
                  <div className="bg-white p-4 rounded-lg border mb-4">
                    <h3 className="font-semibold text-orange-700 mb-3 flex items-center">
                      <Mail className="mr-2" size={18} />
                      Recipients
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-gray-500">Original Email(s)</Label>
                        <Input 
                          value={selectedProposal.emails}
                          disabled
                          className="bg-gray-100 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Add More Email(s) - Comma separated</Label>
                        <Input 
                          value={followupData.additional_emails}
                          onChange={(e) => setFollowupData({ ...followupData, additional_emails: e.target.value })}
                          placeholder="email1@example.com, email2@example.com"
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Custom Message */}
                  <div className="bg-white p-4 rounded-lg border mb-4">
                    <h3 className="font-semibold text-orange-700 mb-3 flex items-center">
                      <Edit3 className="mr-2" size={18} />
                      Custom Message (Optional)
                    </h3>
                    <div className="space-y-2">
                      <Textarea 
                        value={followupData.custom_message}
                        onChange={(e) => setFollowupData({ ...followupData, custom_message: e.target.value })}
                        placeholder="Add a personal note that will appear at the top of the follow-up email..."
                        rows={4}
                        className="text-sm"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={updateFollowupPreview}
                        className="w-full"
                      >
                        <RefreshCw className="mr-1" size={14} />
                        Update Preview
                      </Button>
                    </div>
                  </div>

                  {/* Send Button */}
                  <Button 
                    className="w-full"
                    style={{ backgroundColor: '#f97316' }}
                    onClick={handleSendFollowup}
                    disabled={followupLoading}
                  >
                    <Send className="mr-2" size={16} />
                    {followupLoading ? 'Sending...' : 'Send Follow-up Email'}
                  </Button>
                </div>

                {/* Right Panel - Follow-up Email Preview */}
                <div className="w-3/5 overflow-auto bg-gray-100 p-4">
                  <div className="bg-white rounded-lg shadow mb-4 p-3">
                    <h3 className="font-semibold text-gray-700 flex items-center">
                      <Eye className="mr-2" size={18} />
                      Follow-up Email Preview
                    </h3>
                  </div>
                  
                  {followupLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <RefreshCw className="animate-spin mx-auto mb-3" size={32} />
                        <p className="text-gray-600">Loading preview...</p>
                      </div>
                    </div>
                  ) : followupPreviewHtml ? (
                    <div className="bg-white rounded-lg shadow-lg mx-auto" style={{ maxWidth: '650px' }}>
                      <div 
                        dangerouslySetInnerHTML={{ __html: followupPreviewHtml }}
                        className="email-preview p-4"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64">
                      <p className="text-gray-500">Failed to load preview</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Copy Dialog Modal */}
        {showCopyDialog && selectedProposal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Copy Proposal</h3>
              <p className="text-gray-600 mb-4">
                You are copying the proposal sent to <strong>{selectedProposal.company_name}</strong>.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Would you like to save this as a draft (so you can access it later) or just open it in the editor?
              </p>
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => handleCopyAsDraft(selectedProposal, true)}
                  style={{ backgroundColor: '#059669' }}
                  className="w-full"
                >
                  <Save className="mr-2" size={18} />
                  Save as Draft & Edit
                </Button>
                <Button 
                  onClick={() => handleCopyAsDraft(selectedProposal, false)}
                  variant="outline"
                  className="w-full"
                >
                  <Edit3 className="mr-2" size={18} />
                  Just Open in Editor (Do Not Save)
                </Button>
                <Button 
                  onClick={() => setShowCopyDialog(false)}
                  variant="ghost"
                  className="w-full text-gray-500"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdProposalReports;
